import React, {useState} from 'react';
import Button from '../components/Button';
import TextInput from '../components/TextInput';
import {
  ANSWER_LABEL_MAX_LENGTH,
  QUESTION_TITLE_MAX_LENGTH,
  QUESTION_TITLE_MIN_LENGTH,
  TRANSLATION_LANGUAGES,
} from '../constants';
import {modalStyles, translationsStyles} from '../styles';
import {
  BonusQuestionType,
  QuestionTitleValues,
  QuizOptionValues,
  TranslationLanguage,
} from '../types';
import {countCharacters, truncateCharacters} from '../utils';

interface TranslationsModalProps {
  questionType: BonusQuestionType;
  title: QuestionTitleValues;
  quizOptions: QuizOptionValues[];
  onChangeTitle: (language: TranslationLanguage, value: string) => void;
  onChangeQuizOptionLabel: (
    optionKey: string,
    language: TranslationLanguage,
    value: string,
  ) => void;
  onCancel: () => void;
  onValidate: () => void;
}

export const TranslationsModal = ({
  questionType,
  title,
  quizOptions,
  onChangeTitle,
  onChangeQuizOptionLabel,
  onCancel,
  onValidate,
}: TranslationsModalProps) => {
  const [language, setLanguage] = useState<TranslationLanguage>('fr');

  const isMissing = (value: string) => language !== 'fr' && value.trim() === '';

  const titleLength = countCharacters(title[language]);
  // Le back impose aussi le minimum sur les titres traduits (EN/ES).
  const isTitleTooShort =
    titleLength > 0 && titleLength < QUESTION_TITLE_MIN_LENGTH;

  return (
    <div style={modalStyles.overlay}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Gérer les traductions"
        style={{...modalStyles.dialog, ...translationsStyles.dialog}}>
        <h2 style={modalStyles.title}>Gérer les traductions</h2>

        <div style={translationsStyles.flags} role="tablist">
          {TRANSLATION_LANGUAGES.map(option => {
            const isSelected = option.value === language;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={isSelected}
                title={option.label}
                onClick={() => setLanguage(option.value)}
                style={{
                  ...translationsStyles.flagButton,
                  ...(isSelected ? translationsStyles.flagButtonSelected : {}),
                }}>
                <span
                  className={`flag-icon flag-icon-${option.flag} flag-icon-squared`}
                  style={translationsStyles.flag}
                />
                {option.value.toUpperCase()}
              </button>
            );
          })}
        </div>

        <div key={language} style={translationsStyles.fields}>
          <TextInput
            label="Intitulé de la question"
            required
            value={title[language]}
            invalid={isMissing(title[language]) || isTitleTooShort}
            onChange={value =>
              onChangeTitle(
                language,
                truncateCharacters(value, QUESTION_TITLE_MAX_LENGTH),
              )
            }
            helper={`${titleLength}/${QUESTION_TITLE_MAX_LENGTH} — ${QUESTION_TITLE_MIN_LENGTH} caractères minimum`}
          />

          {questionType === 'quiz' &&
            quizOptions.map((option, index) => (
              <div key={option.key} style={translationsStyles.optionRow}>
                <span style={translationsStyles.optionLabel}>
                  Option {index + 1}
                </span>
                <TextInput
                  placeholder={`Placeholder (${ANSWER_LABEL_MAX_LENGTH} caractères max)`}
                  value={option.label[language]}
                  invalid={isMissing(option.label[language])}
                  onChange={value =>
                    onChangeQuizOptionLabel(
                      option.key,
                      language,
                      truncateCharacters(value, ANSWER_LABEL_MAX_LENGTH),
                    )
                  }
                />
              </div>
            ))}
        </div>

        <div style={modalStyles.actions}>
          <Button variant="secondary" onClick={onCancel}>
            Annuler
          </Button>
          <Button onClick={onValidate}>Valider</Button>
        </div>
      </div>
    </div>
  );
};

export default TranslationsModal;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/Form/TranslationsModal.tsx